import type { ButtonHTMLAttributes, FC } from 'react';

import cn from 'classnames';

// style constants shared with PillLink
export type PillButtonVariant = 'primary' | 'secondary' | 'dark';
export type PillButtonSize = 'sm' | 'md' | 'lg';

interface PillButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant: PillButtonVariant;
  size?: PillButtonSize;
  fullWidth?: boolean;
  // Lets the label wrap onto more lines when it needs to: the pill grows from the size's height instead of clipping.
  allowWrap?: boolean;
}

// biome-ignore lint/style/useComponentExportOnlyModules: style constants shared with PillLink and PostInput
export const pillButtonBaseClass =
  'inline-flex rounded-full px-5 text-[13px] font-medium leading-4 tracking-[-0.13px] border backdrop-blur-[20px] cursor-pointer items-center justify-center disabled:cursor-not-allowed disabled:opacity-60';

// biome-ignore lint/style/useComponentExportOnlyModules: style constant shared across pill buttons
export const pillButtonInteraction = 'transition-[opacity,transform] duration-150 hover:opacity-90 active:scale-[0.99]';

// biome-ignore lint/style/useComponentExportOnlyModules: style constants shared with PillLink and PostInput
export const pillButtonSizes: Record<PillButtonSize, string> = {
  sm: 'h-8',
  md: 'h-10',
  lg: 'h-12',
};

// biome-ignore lint/style/useComponentExportOnlyModules: style constants shared with PillLink and PostInput
export const pillButtonWrapSizes: Record<PillButtonSize, string> = {
  sm: 'min-h-8 py-2',
  md: 'min-h-10 py-2',
  lg: 'min-h-12 py-2',
};

// biome-ignore lint/style/useComponentExportOnlyModules: style constants shared with PillLink and PostInput
export const pillButtonVariants: Record<PillButtonVariant, string> = {
  primary:
    'bg-community-primary text-community-text-on-primary/90 border-community-text-on-primary/10 shadow-community-button',
  secondary:
    'bg-white/80 text-black border-black/10 shadow-[0px_0px_0px_0px_rgba(0,0,0,0.02),0px_2px_6px_0px_rgba(0,0,0,0.04),0px_4px_8px_0px_rgba(0,0,0,0.1)]',
  // For controls laid over light content, where the white secondary pill disappears.
  dark: 'bg-black/60 text-white border-white/20 shadow-[0px_2px_6px_0px_rgba(0,0,0,0.04),0px_4px_8px_0px_rgba(0,0,0,0.1)]',
};

export const PillButton: FC<PillButtonProps> = ({
  variant,
  size = 'md',
  fullWidth = false,
  allowWrap = false,
  className,
  children,
  ...props
}) => {
  return (
    <button
      type="button"
      className={cn(
        pillButtonBaseClass,
        allowWrap ? `${pillButtonWrapSizes[size]} text-center` : pillButtonSizes[size],
        pillButtonVariants[variant],
        { 'w-full': fullWidth },
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
};
